import React, { useState, useMemo } from 'react';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import LearningStatusBadge from '../components/ui/LearningStatusBadge';
import OptimizedImage from '../components/ui/OptimizedImage';
import { 
    StarIcon, 
    LeafIcon, 
    IconWater, 
    IconSun, 
    IconFlame, 
    IconTrophy, 
    IconZap, 
    IconSearch,
    IconFilter,
    IconClock,
    IconChevronRight
} from '../components/ui/Icons';
import { API_URL } from '../utils/api';
import { getLocalizedValue } from '../utils/plantFormatting';

import { useNavigate, Link } from 'react-router-dom';
import ExportModal from '../components/common/ExportModal';

const getCategoryIcon = (categoryName) => {
    if (!categoryName) return '';
    const name = categoryName.toLowerCase();
    if (name.includes('nadel')) return '🌲';
    if (name.includes('laub')) return '🌳';
    if (name.includes('staude')) return '🌸';
    if (name.includes('wasser')) return '💧';
    if (name.includes('kletter')) return '🌿';
    if (name.includes('farn')) return '🌱';
    if (name.includes('gräser') || name.includes('gras')) return '🌾';
    if (name.includes('zimmer')) return '🪴';
    return '🍃';
};

const WelcomeHero = ({ user, userProfile, stats, dueCount, onFilterDue }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const navigate = useNavigate();
    const hour = new Date().getHours();
    const greeting = hour < 12 ? 'Guten Morgen' : hour < 18 ? 'Guten Tag' : 'Guten Abend';
    const name = userProfile?.first_name || user?.first_name || user?.username || 'Gärtner';
    const levelProgress = userProfile?.total_xp ? ((userProfile.total_xp % 1000) / 1000) * 100 : 0;
    const streakDays = userProfile?.streak_days || 0;

    return (
        <div 
            onClick={() => setIsExpanded(!isExpanded)}
            className="relative mb-6 md:mb-8 overflow-hidden rounded-3xl sm:rounded-[2.5rem] bg-gradient-to-br from-plantlix-900 via-plantlix-800 to-emerald-900 p-5 sm:p-6 md:p-8 shadow-2xl shadow-plantlix-900/20 cursor-pointer transition-all hover:shadow-plantlix-900/30 group"
        >
            {/* Decorative Elements */}
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-plantlix-500/20 rounded-full blur-[100px] pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-lime-500/10 rounded-full blur-[80px] pointer-events-none" />
            
            {/* Expand Indicator */}
            <div className="absolute top-6 right-6 md:top-8 md:right-8 text-white/40 group-hover:text-white transition-colors">
                <IconChevronRight className={`w-6 h-6 transition-transform duration-500 ${isExpanded ? '-rotate-90' : 'rotate-90'}`} />
            </div>
            
            <div className="relative z-10">
                <div className="max-w-2xl pr-10">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-white leading-[1.1] mb-2">
                        {greeting}, <span className="text-transparent bg-clip-text bg-gradient-to-r from-plantlix-300 to-lime-300">{name}</span>!
                    </h1>
                    <p className="text-sm md:text-base text-plantlix-100/80 font-medium mb-4">
                        Heute ist ein perfekter Tag, um dein botanisches Wissen zu vertiefen. Du hast bereits <span className="text-white font-bold">{stats.learned} Pflanzen</span> gemeistert.
                    </p>
                    {dueCount > 0 && (
                        <button 
                            onClick={(e) => { e.stopPropagation(); navigate('/quiz?mode=today'); }} 
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-lime-400 text-plantlix-900 font-black text-sm hover:bg-lime-300 transition-colors shadow-lg shadow-lime-400/20"
                        >
                            <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                            Fällige Pflanzen lernen
                        </button>
                    )}
                </div>

                {/* Stats Grid - Collapsible */}
                <div className={`transition-all duration-500 ease-in-out overflow-hidden ${isExpanded ? 'max-h-[500px] opacity-100 mt-6 md:mt-8' : 'max-h-0 opacity-0 mt-0'}`}>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4 w-full" onClick={e => e.stopPropagation()}>
                        <StatCard 
                            icon={<IconTrophy className="w-5 h-5 text-amber-400" />} 
                            label="Level" 
                            value={userProfile?.level || 1} 
                            subValue={`${userProfile?.total_xp || 0} XP`}
                            progress={levelProgress}
                            onClick={() => navigate('/badges')}
                        />
                        <StatCard 
                            icon={<IconFlame className="w-5 h-5 text-orange-500" />} 
                            label="Streak" 
                            value={`${streakDays} Tage`} 
                            subValue="Dranbleiben!"
                            pulseIcon={streakDays > 0}
                            onClick={() => navigate('/profile')}
                        />
                        <StatCard 
                            icon={<IconZap className="w-5 h-5 text-plantlix-400" />} 
                            label="Fortschritt" 
                            value={`${stats.progressPercent}%`} 
                            subValue={`${stats.learned}/${stats.total}`}
                            progress={stats.progressPercent}
                            onClick={() => navigate('/career')}
                        />
                        <StatCard 
                            icon={<IconClock className="w-5 h-5 text-rose-400" />} 
                            label="Fällig" 
                            value={dueCount} 
                            subValue="Wiederholen"
                            highlight={dueCount > 0}
                            onClick={onFilterDue}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ icon, label, value, subValue, highlight, progress, pulseIcon, onClick }) => (
    <div onClick={onClick} className={`group relative p-4 md:p-5 rounded-[1.5rem] bg-white/10 backdrop-blur-xl border border-white/10 transition-all hover:bg-white/15 hover:scale-[1.02] cursor-pointer overflow-hidden ${highlight ? 'ring-2 ring-rose-500/50' : ''}`}>
        <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
            <div className={`p-1.5 md:p-2 rounded-xl bg-black/20 group-hover:bg-black/30 transition-colors shrink-0 ${pulseIcon ? 'animate-pulse' : ''}`}>
                {icon}
            </div>
            <span className="text-[9px] md:text-[10px] font-bold text-white/60 uppercase tracking-widest truncate" title={label}>{label}</span>
        </div>
        <div className="flex flex-col">
            <span className="text-xl md:text-2xl font-black text-white">{value}</span>
            <span className="text-[8px] md:text-[9px] font-bold text-white/40 uppercase mt-0.5 tracking-wider truncate">{subValue}</span>
        </div>
        {progress !== undefined && (
            <div className="absolute bottom-0 left-0 w-full h-1 bg-black/20">
                <div className="h-full bg-lime-400/80 rounded-r-full transition-all duration-1000 ease-out" style={{ width: `${progress}%` }} />
            </div>
        )}
    </div>
);

const Home = ({ plants, userPlantData, categories, trackInteraction, handlePlantClick, handleUpdateUserPlantData, selectedPlantList, setSelectedPlantList, listStats, user, userProfile, sponsors }) => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterFavorites, setFilterFavorites] = useState(false);
    const [filterDueOnly, setFilterDueOnly] = useState(false);
    const [filterZwischenpruefung, setFilterZwischenpruefung] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [filterExpanded, setFilterExpanded] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);

    // Filter Logic
    const isDue = (data) => {
        if (!data || !data.next_review_at) return true;
        const dueTime = new Date(data.next_review_at).getTime();
        return Date.now() >= dueTime;
    };

    // List-specific stats
    const listPlants = useMemo(() => {
        return selectedPlantList
            ? plants.filter(p => p.plant_lists && p.plant_lists.includes(selectedPlantList))
            : plants;
    }, [plants, selectedPlantList]);

    const stats = useMemo(() => {
        const total = listPlants.length;
        const learned = listPlants.filter(p => userPlantData[p.id]?.learning_status === 'LEARNED').length;
        const inProgress = listPlants.filter(p => userPlantData[p.id]?.learning_status === 'IN_PROGRESS').length;
        const progressPercent = total > 0 ? Math.round((learned / total) * 100) : 0;
        
        return { total, learned, inProgress, progressPercent };
    }, [listPlants, userPlantData]);

    const dueCount = useMemo(() => {
        return listPlants.filter(p => isDue(userPlantData[p.id])).length;
    }, [listPlants, userPlantData]);

    const filteredAndSortedPlants = useMemo(() => {
        let result = [...listPlants];

        if (searchTerm) {
            const lowerSearchTerm = searchTerm.toLowerCase();
            result = result.filter(p =>
                p.botanical_name.toLowerCase().includes(lowerSearchTerm) ||
                p.german_name.toLowerCase().includes(lowerSearchTerm) ||
                p.family.toLowerCase().includes(lowerSearchTerm)
            );
        }

        if (filterCategory !== 'all') result = result.filter(p => p.category === parseInt(filterCategory));
        if (filterStatus !== 'all') result = result.filter(p => (userPlantData[p.id]?.learning_status || 'NOT_LEARNED') === filterStatus);
        if (filterFavorites) result = result.filter(p => userPlantData[p.id]?.is_favorite);
        if (filterDueOnly) result = result.filter(p => isDue(userPlantData[p.id]));
        if (filterZwischenpruefung) result = result.filter(p => p.is_intermediate_exam_subject);

        if (sortConfig.key) {
            result.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (sortConfig.key === 'is_favorite') {
                    aValue = userPlantData[a.id]?.is_favorite || false;
                    bValue = userPlantData[b.id]?.is_favorite || false;
                } else if (sortConfig.key === 'learning_status') {
                    aValue = userPlantData[a.id]?.learning_status || 'NOT_LEARNED';
                    bValue = userPlantData[b.id]?.learning_status || 'NOT_LEARNED';
                }

                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return result;
    }, [listPlants, userPlantData, searchTerm, filterCategory, filterStatus, filterFavorites, filterDueOnly, filterZwischenpruefung, sortConfig]);

    const availableCategories = useMemo(() => {
        const categoryIds = new Set(listPlants.map(p => p.category));
        return categories.filter(c => categoryIds.has(c.id));
    }, [listPlants, categories]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return ' ';
        return sortConfig.direction === 'ascending' ? '▲' : '▼';
    };

    const DesktopRow = useMemo(() => React.memo(({ index, style, data: { plants, userPlantData, handlePlantClick, handleUpdateUserPlantData, trackInteraction } }) => {
        const plant = plants[index];
        const data = userPlantData[plant.id] || { learning_status: 'NOT_LEARNED', is_favorite: false };
        
        return (
            <div style={style}>
                <div 
                    className="h-[65px] bg-white dark:bg-[#1a1a1a] border-b border-gray-100 dark:border-gray-800 hover:bg-plantlix-50 dark:hover:bg-plantlix-950/20 transition-all cursor-pointer grid items-center pr-4 group/row" 
                    style={{ gridTemplateColumns: '40px 100px 60px minmax(140px,1.5fr) minmax(140px,1.5fr) minmax(120px,1fr) minmax(100px,1fr) minmax(120px,1fr) 100px' }}
                    onClick={() => { handlePlantClick(plant); trackInteraction('PLANT_VIEW', { plant_id: plant.id }); }}
                >
                    <div className="px-3 xl:px-4 text-center flex items-center justify-center">
                        <StarIcon filled={data.is_favorite} onClick={(e) => { e.stopPropagation(); handleUpdateUserPlantData(plant.id, { is_favorite: !data.is_favorite }) }} className={data.is_favorite ? 'text-amber-500 hover:scale-110 transition-transform' : 'text-gray-300 dark:text-gray-600 hover:text-amber-400'} />
                    </div>
                    <div className="px-3 xl:px-4 flex items-center">
                        <LearningStatusBadge status={data.learning_status} showLabel={false} size="sm" plantId={plant.id} onUpdateStatus={handleUpdateUserPlantData} />
                    </div>
                    <div className="px-2 xl:px-3 text-center flex items-center justify-center">
                        {plant.images && plant.images.length > 0 ? (
                            <OptimizedImage
                                src={plant.images[0].image_small}
                                fallbackSrc={plant.images[0].image}
                                alt={plant.german_name}
                                className="w-11 h-11 object-cover rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 group-hover/row:scale-110 transition-transform duration-300"
                                loading="lazy"
                            />
                        ) : (
                            <div className="w-11 h-11 bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center text-gray-400 text-xs border border-gray-200 dark:border-gray-800">📷</div>
                        )}
                    </div>
                    <div className="px-3 xl:px-4 font-bold text-gray-900 dark:text-gray-100 line-clamp-1">{plant.botanical_name}</div>
                    <div className="px-3 xl:px-4 text-gray-700 dark:text-gray-300 line-clamp-1 font-medium">{plant.german_name}</div>
                    <div className="px-3 xl:px-4 text-gray-600 dark:text-gray-400 line-clamp-1 text-xs uppercase tracking-wider font-bold opacity-70">{plant.family}</div>
                    <div className="px-3 xl:px-4">
                        <div className="flex items-center gap-2">
                            {plant.winter_hardiness && <span className="text-[10px] font-black bg-gray-100 dark:bg-white/10 px-1.5 py-0.5 rounded-md text-gray-600 dark:text-gray-400" title={`Winterhärte: ${getLocalizedValue(plant, 'winter_hardiness')}`}>{getLocalizedValue(plant, 'winter_hardiness')}</span>}
                            {plant.watering_needs && <IconWater className="w-4 h-4 text-cyan-500/80" title={`Wasserbedarf: ${plant.watering_needs}`} />}
                            {plant.location_preference && <IconSun className="w-4 h-4 text-amber-500/80" title={`Standort: ${getLocalizedValue(plant, 'location_preference')}`} />}
                        </div>
                    </div>
                    <div className="px-3 xl:px-4 text-gray-600 dark:text-gray-400 line-clamp-1 text-sm font-medium flex items-center gap-1">
                        <span>{getCategoryIcon(plant.category_name)}</span>
                        {plant.category_name}
                    </div>
                    <div className="px-3 xl:px-4 text-center">
                        {plant.is_intermediate_exam_subject && (
                            <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-plantlix-100 dark:bg-plantlix-900/30 text-plantlix-600 dark:text-plantlix-400 text-xs font-black shadow-sm">Z</div>
                        )}
                    </div>
                </div>
            </div>
        );
    }), []);

    const MobileRow = useMemo(() => React.memo(({ index, style, data: { plants, userPlantData, handlePlantClick, handleUpdateUserPlantData, trackInteraction } }) => {
        const plant = plants[index];
        const data = userPlantData[plant.id] || { learning_status: 'NOT_LEARNED', is_favorite: false };
        const isMastered = data.learning_status === 'LEARNED';
        
        return (
            <div style={{...style, padding: '0 4px 12px 4px'}}>
                <div
                    onClick={() => { handlePlantClick(plant); trackInteraction('PLANT_VIEW', { plant_id: plant.id }); }}
                    className={`group bg-white h-full flex flex-col dark:bg-[#1e1e1e] rounded-3xl overflow-hidden border border-gray-200 dark:border-white/5 shadow-2xl shadow-gray-200/50 dark:shadow-none active:scale-[0.98] transition-all cursor-pointer relative ${isMastered ? 'ring-2 ring-plantlix-500/30' : ''}`}
                >
                    <div className="relative w-full h-[200px] flex-shrink-0 bg-gray-100 dark:bg-gray-800">
                        {plant.images && plant.images.length > 0 ? (
                            <OptimizedImage
                                src={plant.images[0].image_medium || plant.images[0].image_small}
                                fallbackSrc={plant.images[0].image}
                                alt={plant.german_name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                loading="lazy"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                                <span className="text-6xl">📷</span>
                            </div>
                        )}

                        {/* Top Overlays */}
                        <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
                            <div className="flex flex-col gap-2">
                                {plant.is_intermediate_exam_subject && (
                                    <div className="px-3 py-1.5 bg-plantlix-600/90 backdrop-blur-md text-white text-[10px] font-black uppercase tracking-widest rounded-full shadow-lg">
                                        Zwischenprüfung
                                    </div>
                                )}
                                <div className="px-3 py-1.5 bg-white/90 dark:bg-black/60 backdrop-blur-md text-gray-900 dark:text-white text-[10px] font-black uppercase tracking-widest rounded-full shadow-lg border border-white/20 flex items-center gap-1.5">
                                    <span>{getCategoryIcon(plant.category_name)}</span>
                                    {plant.category_name}
                                </div>
                            </div>

                            <button
                                onClick={(e) => { e.stopPropagation(); handleUpdateUserPlantData(plant.id, { is_favorite: !data.is_favorite }) }}
                                className={`w-12 h-12 flex items-center justify-center rounded-full shadow-lg active:scale-90 transition-all ${data.is_favorite ? 'bg-amber-500 text-white' : 'bg-white/90 dark:bg-black/60 backdrop-blur-md text-gray-400 border border-white/20'}`}
                                aria-label={data.is_favorite ? "Von Favoriten entfernen" : "Zu Favoriten hinzufügen"}
                            >
                                <StarIcon filled={data.is_favorite} className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Image Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60" />
                    </div>

                    <div className="p-4 sm:p-6 flex flex-col justify-between flex-grow">
                        <div className="mb-3">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-[10px] font-black text-plantlix-600 dark:text-plantlix-400 uppercase tracking-[0.2em]">
                                    {plant.family}
                                </span>
                            </div>
                            <h3 className="text-xl sm:text-2xl font-black text-gray-900 dark:text-white leading-tight mb-1">
                                {plant.german_name}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 font-bold italic font-serif">
                                {plant.botanical_name}
                            </p>
                        </div>

                        <div className="space-y-3">
                            <LearningStatusBadge
                                status={data.learning_status}
                                showLabel={true}
                                size="md"
                                plantId={plant.id}
                                onUpdateStatus={handleUpdateUserPlantData}
                                className="w-full justify-center min-h-[44px] text-sm font-black rounded-xl shadow-sm"
                            />

                            <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-white/5">
                                <div className="flex items-center gap-3">
                                    {plant.winter_hardiness && (
                                        <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/5">
                                            <span className="text-[10px] font-black text-gray-400 uppercase">WHZ</span>
                                            <span className="text-xs font-black text-gray-700 dark:text-gray-200">
                                                {getLocalizedValue(plant, 'winter_hardiness')}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                        {plant.watering_needs && <IconWater className="w-5 h-5 text-cyan-500" />}
                                        {plant.location_preference && <IconSun className="w-5 h-5 text-amber-500" />}
                                    </div>
                                </div>

                                <div className="flex items-center gap-1 text-xs font-black text-plantlix-500 uppercase tracking-widest">
                                    <span>Details</span>
                                    <IconChevronRight className="w-4 h-4" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }), []);

    return (
        <div className="min-h-screen bg-[#f8fafc] dark:bg-[#0a0a0a]">
            {/* Ambient Background */}
            <div className="fixed top-0 left-0 w-full h-full pointer-events-none -z-10 overflow-hidden">
                <div className="absolute top-[-10%] right-[-10%] w-[50%] h-[50%] bg-plantlix-500/5 dark:bg-plantlix-500/10 rounded-full blur-[120px]" />
                <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-lime-500/5 dark:bg-lime-500/10 rounded-full blur-[100px]" />
            </div>
            
            <div className="p-4 sm:p-6 md:p-8 max-w-7xl mx-auto w-full relative z-10 pb-20">
                
                {/* 1. Welcome Hero */}
                <WelcomeHero 
                    user={user} 
                    userProfile={userProfile} 
                    stats={stats} 
                    dueCount={dueCount} 
                    onFilterDue={() => {
                        setFilterExpanded(true);
                        setFilterDueOnly(true);
                        setFilterCategory('all');
                        setFilterStatus('all');
                        setFilterFavorites(false);
                        setFilterZwischenpruefung(false);
                        setTimeout(() => {
                            window.scrollTo({ top: 300, behavior: 'smooth' });
                        }, 100);
                    }}
                />

                {/* 2. Enhanced Filter Bar (Collapsible on Desktop, Toggle on Mobile) */}
                <div className="relative z-20 mb-6 md:mb-8">
                    {/* Search & Main Filter Row */}
                    <div className="flex flex-col md:flex-row gap-3 md:gap-4 mb-4">
                        <div className="relative flex-grow group">
                            <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-plantlix-500 transition-colors">
                                <IconSearch className="w-5 h-5" />
                            </div>
                            <input
                                type="text"
                                placeholder="Suche nach Namen..."
                                className="w-full pl-12 pr-4 py-3 md:py-4 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-white/10 rounded-2xl md:rounded-3xl shadow-xl shadow-gray-200/30 dark:shadow-none focus:ring-4 focus:ring-plantlix-500/10 focus:border-plantlix-500 transition-all text-gray-900 dark:text-white font-medium placeholder-gray-400"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setFilterExpanded(!filterExpanded)}
                                className={`flex items-center gap-2 px-4 py-3 md:px-6 md:py-4 rounded-2xl md:rounded-3xl font-bold transition-all border shadow-lg ${filterExpanded ? 'bg-plantlix-600 text-white border-plantlix-600' : 'bg-white dark:bg-[#1a1a1a] text-gray-700 dark:text-gray-200 border-gray-200 dark:border-white/10 hover:border-plantlix-500'}`}
                            >
                                <IconFilter className="w-5 h-5" />
                                <span className="hidden sm:inline">Filter</span>
                                {(filterCategory !== 'all' || filterStatus !== 'all' || filterFavorites || filterDueOnly || filterZwischenpruefung) && (
                                    <span className="w-2 h-2 rounded-full bg-lime-400 animate-pulse" />
                                )}
                            </button>
                            <button
                                onClick={() => setIsExportModalOpen(true)}
                                className="px-4 py-3 md:px-6 md:py-4 bg-white dark:bg-[#1a1a1a] text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-white/10 rounded-2xl md:rounded-3xl font-bold shadow-lg hover:border-plantlix-500 transition-all flex items-center gap-2"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                <span className="hidden sm:inline">Export</span>
                            </button>
                        </div>
                    </div>

                    {/* Expanded Filter Panel */}
                    {filterExpanded && (
                        <div className="p-6 bg-white/80 dark:bg-[#1a1a1a]/80 backdrop-blur-2xl rounded-[2rem] border border-gray-200 dark:border-white/10 shadow-2xl animate-in slide-in-from-top-4 duration-300 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <FilterGroup label="Pflanzenliste">
                                    <select
                                        value={selectedPlantList}
                                        onChange={(e) => setSelectedPlantList(e.target.value)}
                                        className="w-full p-3.5 bg-gray-50 dark:bg-black/20 border border-gray-200 dark:border-white/5 rounded-2xl focus:ring-2 focus:ring-plantlix-500/20 text-sm font-bold"
                                    >
                                        <option value="GALABAUER">GaLaBau Sachsen {listStats ? `(${listStats['GALABAUER']})` : ''}</option>
                                        <option value="FRIEDHOFSGAERTNER" disabled={listStats && listStats['FRIEDHOFSGAERTNER'] === 0}>Friedhof Sachsen {listStats ? `(${listStats['FRIEDHOFSGAERTNER']})` : ''}</option>
                                        <option value="ZIERPFLANZENGAERTNER" disabled={listStats && listStats['ZIERPFLANZENGAERTNER'] === 0}>Zierpflanzen Sachsen {listStats ? `(${listStats['ZIERPFLANZENGAERTNER']})` : ''}</option>
                                    </select>
                                </FilterGroup>

                                <FilterGroup label="Kategorie">
                                    <select
                                        className="w-full p-3.5 bg-gray-50 dark:bg-black/20 border border-gray-200 dark:border-white/5 rounded-2xl focus:ring-2 focus:ring-plantlix-500/20 text-sm font-bold"
                                        value={filterCategory}
                                        onChange={(e) => setFilterCategory(e.target.value)}
                                    >
                                        <option value="all">Alle Kategorien</option>
                                        {availableCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                                    </select>
                                </FilterGroup>

                                <FilterGroup label="Lernstatus">
                                    <select
                                        className="w-full p-3.5 bg-gray-50 dark:bg-black/20 border border-gray-200 dark:border-white/5 rounded-2xl focus:ring-2 focus:ring-plantlix-500/20 text-sm font-bold"
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                    >
                                        <option value="all">Alle Status</option>
                                        <option value="NOT_LEARNED">Nicht gelernt</option>
                                        <option value="IN_PROGRESS">In Arbeit</option>
                                        <option value="LEARNED">Gelernt</option>
                                    </select>
                                </FilterGroup>
                            </div>

                            <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-gray-100 dark:border-white/5">
                                <div className="flex flex-wrap gap-3">
                                    <FilterToggle active={filterFavorites} onClick={() => setFilterFavorites(!filterFavorites)} icon="⭐" label="Favoriten" />
                                    <FilterToggle active={filterDueOnly} onClick={() => setFilterDueOnly(!filterDueOnly)} icon="📅" label="Fällig" />
                                    <FilterToggle active={filterZwischenpruefung} onClick={() => setFilterZwischenpruefung(!filterZwischenpruefung)} icon="🎓" label="Zwischenprüfung" />
                                </div>
                                <button
                                    onClick={() => {
                                        setFilterCategory('all');
                                        setFilterStatus('all');
                                        setFilterFavorites(false);
                                        setFilterDueOnly(false);
                                        setFilterZwischenpruefung(false);
                                        setSearchTerm('');
                                    }}
                                    className="text-xs font-black text-gray-400 hover:text-plantlix-500 uppercase tracking-widest transition-colors px-4 py-2"
                                >
                                    Filter zurücksetzen
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Desktop Table View */}
                <div className="hidden md:flex flex-col flex-1 bg-white dark:bg-[#1a1a1a] rounded-[2.5rem] border border-gray-100 dark:border-white/5 shadow-2xl shadow-gray-200/30 dark:shadow-none overflow-hidden min-h-[600px] animate-in fade-in zoom-in-95 duration-700" style={{ height: 'calc(100vh - 340px)' }}>
                    <div className="w-full text-sm text-left text-gray-500 dark:text-gray-400 min-w-[1000px] flex flex-col h-full">
                        {/* Header Grid */}
                        <div className="text-[10px] text-gray-400 dark:text-gray-500 font-black uppercase tracking-[0.2em] bg-gray-50/50 dark:bg-black/20 border-b border-gray-100 dark:border-white/5 grid pr-4 flex-shrink-0" style={{ gridTemplateColumns: '40px 100px 60px minmax(140px,1.5fr) minmax(140px,1.5fr) minmax(120px,1fr) minmax(100px,1fr) minmax(120px,1fr) 100px' }}>
                            <div className="px-3 xl:px-4 py-5 cursor-pointer hover:text-plantlix-500 transition-colors" onClick={() => requestSort('is_favorite')}>{getSortIndicator('is_favorite')}</div>
                            <div className="px-3 xl:px-4 py-5 cursor-pointer hover:text-plantlix-500 transition-colors" onClick={() => requestSort('learning_status')}>Status {getSortIndicator('learning_status')}</div>
                            <div className="px-2 xl:px-3 py-5 text-center">Bild</div>
                            <div className="px-3 xl:px-4 py-5 cursor-pointer hover:text-plantlix-500 transition-colors" onClick={() => requestSort('botanical_name')}>Botanisch {getSortIndicator('botanical_name')}</div>
                            <div className="px-3 xl:px-4 py-5 cursor-pointer hover:text-plantlix-500 transition-colors" onClick={() => requestSort('german_name')}>Deutsch {getSortIndicator('german_name')}</div>
                            <div className="px-3 xl:px-4 py-5 cursor-pointer hover:text-plantlix-500 transition-colors" onClick={() => requestSort('family')}>Familie {getSortIndicator('family')}</div>
                            <div className="px-3 xl:px-4 py-5">Merkmale</div>
                            <div className="px-3 xl:px-4 py-5 cursor-pointer hover:text-plantlix-500 transition-colors" onClick={() => requestSort('category_name')}>Kategorie {getSortIndicator('category_name')}</div>
                            <div className="px-3 xl:px-4 py-5 text-center cursor-pointer hover:text-plantlix-500 transition-colors" onClick={() => requestSort('is_intermediate_exam_subject')}>Prüfung {getSortIndicator('is_intermediate_exam_subject')}</div>
                        </div>
                        {/* Virtualized Body */}
                        <div className="flex-grow w-full relative">
                            <AutoSizer>
                                {({ height, width }) => (
                                    <List
                                        height={height}
                                        width={width}
                                        itemCount={filteredAndSortedPlants.length}
                                        itemSize={65}
                                        overscanCount={8}
                                        itemData={{ 
                                            plants: filteredAndSortedPlants, 
                                            userPlantData, 
                                            handlePlantClick, 
                                            handleUpdateUserPlantData, 
                                            trackInteraction 
                                        }}
                                    >
                                        {DesktopRow}
                                    </List>
                                )}
                            </AutoSizer>
                        </div>
                    </div>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden animate-in fade-in slide-in-from-bottom-8 duration-700" style={{ height: 'calc(100vh - 200px)', minHeight: '600px', width: '100%' }}>
                    <AutoSizer>
                        {({ height, width }) => (
                            <List
                                height={height}
                                width={width}
                                itemCount={filteredAndSortedPlants.length}
                                itemSize={420}
                                overscanCount={3}
                                itemData={{ 
                                    plants: filteredAndSortedPlants, 
                                    userPlantData, 
                                    handlePlantClick, 
                                    handleUpdateUserPlantData, 
                                    trackInteraction 
                                }}
                            >
                                {MobileRow}
                            </List>
                        )}
                    </AutoSizer>
                </div>
            </div>

            <ExportModal 
                isOpen={isExportModalOpen} 
                onClose={() => setIsExportModalOpen(false)} 
                selectedPlantIds={filteredAndSortedPlants.map(p => p.id)} 
                title="Dashboard Export"
            />
        </div>
    );
};

const FilterGroup = ({ label, children }) => (
    <div className="space-y-2">
        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest pl-1">{label}</label>
        {children}
    </div>
);

const FilterToggle = ({ active, onClick, icon, label }) => (
    <button
        onClick={onClick}
        className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-black transition-all border shadow-sm ${active ? 'bg-plantlix-500 text-white border-plantlix-500 shadow-plantlix-500/20' : 'bg-gray-50 dark:bg-white/5 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-white/5 hover:border-gray-300 dark:hover:border-white/20'}`}
    >
        <span>{icon}</span>
        <span className="uppercase tracking-wider">{label}</span>
    </button>
);

export default Home;
